// src/services/automation.ts

import {
  ApiResponse,
  AutomationConfig,
  AutomationLog,
  AutomationReport,
  CreateAutomationConfigDto,
  LogFilters,
  UpdateAutomationConfigDto,
} from "../interfaces/all";
import { apiService } from "./axios";

export const automationApi = {
  // ==================== CONFIGURAÇÕES ====================

  async createConfig(
    config: CreateAutomationConfigDto,
  ): Promise<ApiResponse<AutomationConfig>> {
    return await apiService.post("/automation/configs", config);
  },

  async getConfigs(
    companyId: string,
  ): Promise<ApiResponse<AutomationConfig[]>> {
    return await apiService.get(`/automation/configs/${companyId}`);
  },

  async getConfig(
    companyId: string,
    campaignId: string | null,
    leadStatus: string,
  ): Promise<ApiResponse<AutomationConfig | null>> {
    const campaignParam = campaignId === null ? "null" : campaignId;
    return await apiService.get(
      `/automation/configs/${companyId}/${campaignParam}/${leadStatus}`,
    );
  },

  async updateConfig(
    id: string,
    config: UpdateAutomationConfigDto,
  ): Promise<ApiResponse<AutomationConfig>> {
    return await apiService.put(`/automation/configs/${id}`, config);
  },

  async deleteConfig(id: string): Promise<ApiResponse<void>> {
    return await apiService.delete(`/automation/configs/${id}`);
  },

  async activateConfig(id: string): Promise<ApiResponse<AutomationConfig>> {
    return await apiService.patch(`/automation/configs/${id}/activate`);
  },

  async deactivateConfig(id: string): Promise<ApiResponse<AutomationConfig>> {
    return await apiService.patch(`/automation/configs/${id}/deactivate`);
  },

  // ==================== LOGS E RELATÓRIOS ====================

  async getLogs(
    companyId: string,
    filters?: LogFilters,
  ): Promise<ApiResponse<AutomationLog[]>> {
    const params = new URLSearchParams();
    if (filters?.lead_id) params.append("lead_id", filters.lead_id.toString());
    if (filters?.template_purpose)
      params.append("template_purpose", filters.template_purpose);
    if (filters?.success !== undefined)
      params.append("success", filters.success.toString());
    if (filters?.start_date) params.append("start_date", filters.start_date);
    if (filters?.end_date) params.append("end_date", filters.end_date);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const queryString = params.toString();
    const url = `/automation/logs/${companyId}${queryString ? `?${queryString}` : ""}`;
    return await apiService.get(url);
  },

  async getLogsByLead(leadId: number): Promise<ApiResponse<AutomationLog[]>> {
    return await apiService.get(`/automation/logs/lead/${leadId}`);
  },

  async getAutomationReport(
    companyId: string,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<AutomationReport[]>> {
    return await apiService.get(
      `/automation/reports/${companyId}?start_date=${startDate}&end_date=${endDate}`,
    );
  },

  // ==================== SCHEDULER ====================

  async startScheduler(): Promise<ApiResponse<void>> {
    return await apiService.post("/automation/scheduler/start");
  },

  async stopScheduler(): Promise<ApiResponse<void>> {
    return await apiService.post("/automation/scheduler/stop");
  },

  async runAutomationCheck(): Promise<ApiResponse<void>> {
    return await apiService.post("/automation/scheduler/run");
  },
};
